import { AxiosError } from "axios";

import history from "./history";
import { AuthalError, PaginationRequest, SortOrder, SortPredicate } from "./types";

export const HTTP_BAD_REQUEST = 400;
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_FORBIDDEN = 403;
export const HTTP_NOT_FOUND = 404;
export const HTTP_CONFLICT = 409;
export const HTTP_PRECONDITION_FAILED = 412;
export const HTTP_UNPROCESSABLE_ENTITY = 422;
export const REASON_CODE_LOGIN_FAILED = 42201;
export const REASON_CODE_EXTERNAL_ACCOUNT = 42202;
export const HTTP_UNAVAILABLE_LEGAL_REASONS = 451;
export const REASON_CODE_AGREEMENTS_NOT_SIGNED = 45101;
export const REASON_CODE_MISSING_NAME = 45102;
export const REASON_CODE_BLOCKED_EMAIL_DOMAIN = 40001;

const X_IF_UNMODIFIED_HEADER = "X-If-Unmodified-Since";

export const UNEXPECTED_ERROR = "An unexpected error occurred";

export const DEFAULT_PAGE_PARAMS: PaginationRequest = { pageNumber: 1, pageSize: 20 };

export function redirect(url: string, withReferral = false): void {
  const referral = encodeURIComponent(window.location.href);
  const redirectUrl = withReferral ? `${url}?referral=${referral}` : url;
  if (/^https?:\/\//.test(redirectUrl)) {
    window.location.replace(redirectUrl);
  } else {
    history.push(redirectUrl);
  }
}

export function reportError(error: Error): void {
  console.error(error); // eslint-disable-line no-console
}

export function getErrorStatus(error: AxiosError): number | null {
  return error.response?.status ?? null;
}

export function getIfUnmodifiedHeader(mtime: Readonly<Date>): { [X_IF_UNMODIFIED_HEADER]: string } {
  return { [X_IF_UNMODIFIED_HEADER]: mtime.toISOString() };
}

export function getErrorMessage(error: AxiosError): string | null {
  return error.response?.data.error ?? null;
}

export function getAuthalErrorMessage(error: AuthalError): string {
  // TODO: Update when backend sends consistent form of error result
  const responseData = error.response?.data;
  if (typeof responseData === "object" && "detail" in responseData) {
    return responseData.detail;
  }
  if (typeof responseData === "object" && "errors" in responseData) {
    const authalErrors = responseData.errors;
    switch (typeof authalErrors) {
      case "string":
        return authalErrors;
      case "object":
        return JSON.stringify(authalErrors);
    }
  }
  reportError(error);
  return responseData || UNEXPECTED_ERROR;
}

export function getErrorReasonCode(error: AxiosError): number | undefined {
  return error.response?.data?.reason_code;
}

export function getResponseRedirect(error: AxiosError): string {
  return error.response?.data?.redirect ?? "";
}

export function shouldUseResponseRedirect(error: AxiosError): boolean {
  const isUnprocessableEntityError = getErrorStatus(error) === HTTP_UNPROCESSABLE_ENTITY;
  const isExternalAccountError = getErrorReasonCode(error) === REASON_CODE_EXTERNAL_ACCOUNT;
  const redirectUrl = getResponseRedirect(error);
  return isUnprocessableEntityError && isExternalAccountError && !!redirectUrl;
}

export function formatSortPredicatesURLParams<SortPredicateType extends string>(
  sortBy: ReadonlyArray<SortPredicate<SortPredicateType>>
): string {
  const parsedSortPredicates = sortBy.map(({ sortKey, sortOrder }) => {
    // If sortOrder is DESC, then prepend a "-" to the sortKeys.
    // Otherwise, just return the sortKey as is.
    const sortPrefix = sortOrder === SortOrder.DESC ? "-" : "";
    return `${sortPrefix}${sortKey}`;
  });
  return parsedSortPredicates.toString();
}
